import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n";
import "./pixo.css";

const HERO = (name) => `/pixo/hero/${name}.png`;
const HELLO_MIN_MS = 7000;
const HELLO_MAX_MS = 14000;
const WAVE = ["wave1", "wave2", "wave1", "wave2", "wave1", "wave2", "hold"];

/**
 * Landing-page Pixo (official mascot outfit: tierTCG beret, no gem) holding the tierTCG mark.
 * Breathes, says a random hello every few seconds; a click only plays a hop + wave (no message).
 */
export default function PixoHero({ scale = 5, className = "", lines = null }) {
  const { t } = useI18n();
  const [frame, setFrame] = useState("hold");
  const [bubble, setBubble] = useState("");
  const [hop, setHop] = useState(false);
  const timers = useRef([]);
  const busy = useRef(false);

  const play = useCallback((withHop) => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    busy.current = true;
    if (withHop) setHop(true);
    WAVE.forEach((f, i) => timers.current.push(setTimeout(() => setFrame(f), i * 150)));
    timers.current.push(
      setTimeout(() => {
        setHop(false);
        busy.current = false;
      }, WAVE.length * 150)
    );
  }, []);

  const hello = useCallback(() => {
    const pool = lines || t.pixo.hellos;
    setBubble(pool[Math.floor(Math.random() * pool.length)]);
    play(false);
    timers.current.push(setTimeout(() => setBubble(""), 2600));
  }, [t, play, lines]);

  const poke = () => {
    if (busy.current) return;
    setBubble("");
    play(true);
  };

  useEffect(() => {
    let alive = true;
    let next;
    const schedule = () => {
      next = setTimeout(() => {
        if (!alive) return;
        if (!busy.current) hello();
        schedule();
      }, HELLO_MIN_MS + Math.random() * (HELLO_MAX_MS - HELLO_MIN_MS));
    };
    const first = setTimeout(hello, 1800);
    schedule();
    return () => {
      alive = false;
      clearTimeout(first);
      clearTimeout(next);
      timers.current.forEach(clearTimeout);
    };
  }, [hello]);

  return (
    <div
      className={`pixo-hero ${hop ? "is-hop" : ""} ${className}`}
      style={{ "--s": scale }}
      onClick={poke}
      role="img"
      aria-label="Pixo"
      data-testid="pixo-hero"
    >
      <div className="pixo-hero-shadow" />
      <div className="pixo-stack">
        <img className="w56" src={HERO("mark")} alt="" />
        <img className="w56" src={HERO(`body-${frame}`)} alt="" />
        <img className="w56" src={HERO("beret")} alt="" />
      </div>
      {bubble && (
        <div className="pixo-bubble" role="status">
          <div className="pixo-bubble-box">
            <div className="pixo-bubble-text">{bubble}</div>
          </div>
        </div>
      )}
    </div>
  );
}
